'use client';

import { useMemo } from 'react';

import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Autocomplete, AutocompleteOption } from '@ravago/shared/radiance/components/input-field/components/Autocomplete/Autocomplete';
import { DatePicker } from '@ravago/shared/radiance/components/input-field/components/DatePicker/DatePicker';
import { useTranslations } from '@ravago/shared/translations/client';

import { ProductPriceCardItem } from '../models/product-price-card-item.model';
import { ProductPriceListFiltersValue } from '../models/product-price-list-filters-value';

interface Props {
   productPriceCardItems: ProductPriceCardItem[];
   value: ProductPriceListFiltersValue;
   onChange: (value: ProductPriceListFiltersValue) => void;
}

export const ProductPriceListFilterFields = ({ productPriceCardItems, value, onChange }: Props) => {
   const translate = useTranslations();

   const [brandOptions, polymerTypeOptions] = useMemo(() => {
      const brandsSet = new Set<string>();
      const polymerTypeSet = new Set<string>();

      productPriceCardItems.forEach((item) => {
         if (item.brandName) brandsSet.add(item.brandName);
         if (item.polymerType) polymerTypeSet.add(item.polymerType);
      });

      return [
         [...brandsSet].map((brand) => ({
            id: brand,
            value: brand
         })),
         [...polymerTypeSet].map((polymerType) => ({
            id: polymerType,
            value: polymerType
         }))
      ];
   }, [productPriceCardItems]);

   const selectedBrandOption = value.brand ? brandOptions.find((brand) => brand.id === value.brand) : undefined;
   const selectedPolymerTypeOption = value.polymerType ? polymerTypeOptions.find((polymerType) => polymerType.id === value.polymerType) : undefined;

   const handleBrandChange = (newBrand: AutocompleteOption | undefined) => {
      onChange({ ...value, brand: newBrand?.id ?? '' });
   };

   const handlePolymerTypeChange = (newPolymerType: AutocompleteOption | undefined) => {
      onChange({ ...value, polymerType: newPolymerType ? newPolymerType.id : '' });
   };

   const handleOrderedFromChanged = (newOrderedFrom: string) => {
      onChange({ ...value, orderedFromDate: newOrderedFrom });
   };

   const handleOrderedUntilChanged = (newOrderedUntil: string) => {
      onChange({ ...value, orderedUntilDate: newOrderedUntil });
   };

   return (
      <>
         <BoxItem grow={1} order={0}>
            <Autocomplete
               options={brandOptions}
               label={translate('prices.brand-placeholder')}
               value={selectedBrandOption}
               onChange={handleBrandChange}
               onClear={() => handleBrandChange(undefined)}
               clearable
            />
         </BoxItem>

         <BoxItem grow={1} order={0}>
            <Autocomplete
               options={polymerTypeOptions}
               label={translate('prices.polymer-placeholder')}
               value={selectedPolymerTypeOption}
               onChange={handlePolymerTypeChange}
               onClear={() => handlePolymerTypeChange(undefined)}
               clearable
            />
         </BoxItem>

         <BoxItem grow={1} order={0}>
            <DatePicker
               id="product-price-list-date-ordered-from"
               label={translate('prices.ordered-from')}
               value={value.orderedFromDate}
               onChange={handleOrderedFromChanged}
               clearable
            />
         </BoxItem>

         <BoxItem grow={1} order={0}>
            <DatePicker
               id="product-price-list-date-ordered-until"
               label={translate('prices.ordered-until')}
               value={value.orderedUntilDate}
               onChange={handleOrderedUntilChanged}
               clearable
            />
         </BoxItem>
      </>
   );
};
